/* ==========================================================================
   Kaufwerk – globale Hilfsfunktionen und Komponenten (Vanilla JS)
   ========================================================================== */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  theme.routes = theme.routes || {};
  theme.strings = theme.strings || {};
  theme.settings = theme.settings || {};

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Hilfsfunktionen ---------- */
  const utils = {
    debounce(fn, wait = 300) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    parseHTML(text) {
      return new DOMParser().parseFromString(text, 'text/html');
    },

    jsonFetchOptions(body) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      };
    },

    announce(message) {
      const region = document.getElementById('GlobalLiveRegion');
      if (!region || !message) return;
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 60);
    },

    formatMoney(cents, currency) {
      const code = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || theme.settings.shopCurrency || 'EUR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: code,
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2) + ' ' + code;
      }
    },

    focusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    },

    restoreFocus(previousId, fallback) {
      if (previousId) {
        const el = document.getElementById(previousId);
        if (el) {
          el.focus({ preventScroll: true });
          return;
        }
      }
      if (fallback) fallback.focus({ preventScroll: true });
    },

    reducedMotion,
  };
  theme.utils = utils;

  /* ---------- Skripte bei Bedarf nachladen ---------- */
  const scriptPromises = {};
  theme.loadScript = (url) => {
    if (!url) return Promise.reject(new Error('missing url'));
    if (!scriptPromises[url]) {
      scriptPromises[url] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return scriptPromises[url];
  };

  /* ---------- Dialoge (Drawer, Modals, Zoom) ---------- */
  const openDialogs = new Set();

  const dialog = {
    open(el, opener) {
      if (!el || el.open) return;
      el._opener = opener || document.activeElement;
      el.classList.remove('is-closing');
      el.showModal();
      openDialogs.add(el);
      document.documentElement.classList.add('has-overlay');
      el.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    },

    close(el) {
      if (!el || !el.open || el.classList.contains('is-closing')) return;
      const finish = () => {
        el.classList.remove('is-closing');
        if (el.open) el.close();
      };
      const animated = el.classList.contains('drawer') || el.classList.contains('modal');
      if (!animated || reducedMotion()) {
        finish();
        return;
      }
      el.classList.add('is-closing');
      let done = false;
      const end = () => {
        if (done) return;
        done = true;
        finish();
      };
      el.addEventListener('animationend', end, { once: true });
      window.setTimeout(end, 320);
    },
  };
  theme.dialog = dialog;

  document.addEventListener(
    'close',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLDialogElement)) return;
      openDialogs.delete(el);
      if (!document.querySelector('dialog[open]')) {
        document.documentElement.classList.remove('has-overlay');
      }
      const opener = el._opener;
      el._opener = null;
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
      el.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
    },
    true
  );

  document.addEventListener(
    'cancel',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLDialogElement)) return;
      event.preventDefault();
      dialog.close(el);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-open-dialog]');
    if (openTrigger) {
      const target = document.getElementById(openTrigger.dataset.openDialog);
      if (target) {
        event.preventDefault();
        dialog.open(target, openTrigger);
      }
      return;
    }

    const closeTrigger = event.target.closest('[data-close-dialog]');
    if (closeTrigger) {
      event.preventDefault();
      dialog.close(closeTrigger.closest('dialog'));
      return;
    }

    // Klick auf den Hintergrund schliesst den Dialog
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) dialog.close(event.target);
    }
  });

  /* ---------- Ankündigungsleiste ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.paused = false;
      this.userPaused = false;
      this.interval = Number(this.dataset.autoplay || 0) * 1000;

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
      this.pauseButton = this.querySelector('[data-announcement-pause]');
      this.pauseButton?.addEventListener('click', () => this.togglePause());

      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));

      document.addEventListener('shopify:block:select', (event) => {
        const index = this.items.indexOf(event.target);
        if (index > -1) {
          this.userPaused = true;
          this.show(index);
        }
      });

      if (this.interval && !reducedMotion()) this.start();
      else if (this.pauseButton && reducedMotion()) this.setPausedState(true);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    start() {
      window.clearInterval(this.timer);
      this.timer = window.setInterval(() => {
        if (!this.paused && !this.userPaused && !document.hidden) this.show(this.index + 1);
      }, this.interval);
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      this.setPausedState(this.userPaused);
      if (!this.userPaused && this.interval) this.start();
    }

    setPausedState(paused) {
      this.userPaused = paused;
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause);
    }

    show(index, manual) {
      const count = this.items.length;
      this.index = (index + count) % count;
      this.items.forEach((item, i) => {
        item.hidden = i !== this.index;
        item.classList.toggle('is-active', i === this.index);
      });
      if (manual && this.interval) this.start();
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Sticky Header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky;
      this.lastScroll = window.scrollY;
      this.updateHeight = this.updateHeight.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.updateHeight();
      window.addEventListener('resize', utils.debounce(this.updateHeight, 150));
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateHeight());
        this.resizeObserver.observe(this);
      }

      if (this.mode !== 'none') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    updateHeight() {
      const height = Math.round(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', this.mode === 'none' ? '0px' : height + 'px');
      const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', bottom + 'px');
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const current = window.scrollY;
        const sectionTop = this.section.offsetTop;
        this.section.classList.toggle('is-scrolled', current > sectionTop + 4);

        if (this.mode === 'scroll-up') {
          const busy = this.querySelector('[aria-expanded="true"], details[open]') || this.contains(document.activeElement);
          const goingDown = current > this.lastScroll;
          if (goingDown && current > sectionTop + this.offsetHeight * 2 && !busy) {
            this.section.classList.add('is-hidden');
          } else if (!goingDown) {
            this.section.classList.remove('is-hidden');
          }
        }

        const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
        document.documentElement.style.setProperty('--header-bottom', bottom + 'px');
        this.lastScroll = current;
        this.ticking = false;
      });
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Mega-Menü ---------- */
  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.mega-menu__toggle');
      this.panel = this.querySelector('[data-mega-panel]');
      this.categoryButtons = Array.from(this.querySelectorAll('[data-mega-category]'));
      this.canHover = window.matchMedia('(hover: hover)').matches;

      this.toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));

      this.categoryButtons.forEach((button) => {
        button.addEventListener('click', () => this.activate(button));
        if (this.canHover) {
          button.addEventListener('mouseenter', () => {
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => this.activate(button), 90);
          });
          button.addEventListener('mouseleave', () => window.clearTimeout(this.hoverTimer));
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen()) {
          event.stopPropagation();
          this.close();
          this.toggle.focus();
        }
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && event.target.matches('[data-mega-category]')) {
          event.preventDefault();
          const index = this.categoryButtons.indexOf(event.target);
          const next = this.categoryButtons[(index + (event.key === 'ArrowDown' ? 1 : -1) + this.categoryButtons.length) % this.categoryButtons.length];
          next.focus();
          this.activate(next);
        }
      });

      this.addEventListener('focusout', (event) => {
        if (this.isOpen() && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });

      this.onDocumentClick = (event) => {
        if (this.isOpen() && !this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);

      document.addEventListener('shopify:section:select', (event) => {
        if (this.closest('.shopify-section') === event.target) this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    open() {
      document.querySelectorAll('details-dropdown details[open]').forEach((details) => details.removeAttribute('open'));
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      const active = this.categoryButtons.find((b) => b.getAttribute('aria-expanded') === 'true') || this.categoryButtons[0];
      if (active) this.activate(active);
    }

    close() {
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    activate(button) {
      this.categoryButtons.forEach((other) => {
        const expanded = other === button;
        other.setAttribute('aria-expanded', String(expanded));
        const sub = document.getElementById(other.getAttribute('aria-controls'));
        if (sub) sub.hidden = !expanded;
      });
    }
  }
  if (!customElements.get('mega-menu')) customElements.define('mega-menu', MegaMenu);

  /* ---------- Einfache Dropdowns (details) ---------- */
  class DetailsDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');

      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) {
          document.querySelectorAll('details-dropdown details[open]').forEach((other) => {
            if (other !== this.details) other.removeAttribute('open');
          });
          document.querySelectorAll('mega-menu').forEach((menu) => menu.close && menu.close());
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.removeAttribute('open');
          this.summary.focus();
        }
      });

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.details.removeAttribute('open');
      });

      this.onDocumentClick = (event) => {
        if (this.details.open && !this.contains(event.target)) this.details.removeAttribute('open');
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }
  if (!customElements.get('details-dropdown')) customElements.define('details-dropdown', DetailsDropdown);

  /* ---------- Karussell (Produktreihen) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', utils.debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', utils.debounce(this.update, 150));
      this.update();
    }

    scroll(direction) {
      const amount = this.track.clientWidth * 0.9;
      this.track.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      if (!this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      const scrollable = maxScroll > 0;
      if (this.prev) this.prev.disabled = !scrollable || this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = !scrollable || this.track.scrollLeft >= maxScroll;
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Mengenfeld ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-quantity-minus]');
      this.plus = this.querySelector('[data-quantity-plus]');
      this.minus.addEventListener('click', () => this.step(-1));
      this.plus.addEventListener('click', () => this.step(1));
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }

    step(direction) {
      const step = Number(this.input.step) || 1;
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const current = Number(this.input.value) || 0;
      const next = Math.min(max, Math.max(min, current + direction * step));
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateButtons() {
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const value = Number(this.input.value) || 0;
      this.minus.disabled = value <= min;
      this.plus.disabled = value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Footer-Akkordeon auf Mobil ---------- */
  const initFooterAccordions = () => {
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.querySelectorAll('[data-footer-accordion]').forEach((details) => details.removeAttribute('open'));
    }
  };

  /* ---------- Einblenden beim Scrollen ---------- */
  const initReveal = () => {
    if (!document.body.classList.contains('has-reveal') || reducedMotion() || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    const sections = Array.from(document.querySelectorAll('#MainContent > .shopify-section')).slice(1);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px' }
    );
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top < window.innerHeight) return;
      section.classList.add('reveal');
      observer.observe(section);
    });
  };

  /* ---------- Suche erst bei Interaktion laden ---------- */
  const loadSearch = (event) => {
    if (!event.target.closest || !event.target.closest('predictive-search')) return;
    document.removeEventListener('focusin', loadSearch);
    document.removeEventListener('pointerdown', loadSearch);
    if (theme.assets && theme.assets.search) theme.loadScript(theme.assets.search).catch(() => {});
  };
  if (theme.assets && theme.assets.search && !customElements.get('predictive-search')) {
    document.addEventListener('focusin', loadSearch);
    document.addEventListener('pointerdown', loadSearch);
  }

  const ready = () => {
    initFooterAccordions();
    initReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
